/**
 * LoginPage — magic link re-authentication (/login)
 *
 * Shown when a user's session cookie has expired. Accepts an email address,
 * calls POST /api/auth/request-login, and shows a confirmation state.
 * No AppLayout — this is an unauthenticated page.
 */

import { useState } from 'react';
import { BrandFooter } from '@/components/ui/BrandFooter';

type State = 'idle' | 'loading' | 'sent' | 'error';

export function LoginPage(): React.ReactElement {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<State>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email.trim() || state === 'loading') return;

    setState('loading');
    setErrorMsg(null);

    try {
      const res = await fetch('/api/auth/request-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!res.ok) {
        throw new Error('Something went wrong. Please try again.');
      }

      setState('sent');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
      setState('error');
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[var(--stitch-surface)]">
      <main className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <span className="material-symbols-outlined text-[48px] text-[var(--stitch-primary)]">
              lock_open
            </span>
            <h1 className="stitch-font-display text-[28px] text-[var(--stitch-primary)] uppercase tracking-tight mt-2">
              Sign in
            </h1>
            <p className="stitch-font-body text-[14px] text-[var(--stitch-on-surface-variant)] mt-1">
              We'll email you a link to sign in instantly.
            </p>
          </div>

          {state === 'sent' ? (
            <div className="rounded-xl border border-[var(--stitch-outline-variant)] bg-[var(--stitch-surface-container-lowest)] p-6 text-center">
              <span className="material-symbols-outlined text-[36px] text-green-600 mb-2 block">
                mark_email_read
              </span>
              <p className="stitch-font-headline text-[16px] text-[var(--stitch-on-surface)] mb-1">
                Check your inbox
              </p>
              <p className="stitch-font-body text-[13px] text-[var(--stitch-on-surface-variant)]">
                If an account exists for <strong>{email}</strong>, a sign-in link has been sent. It expires in 15 minutes.
              </p>
            </div>
          ) : (
            <form onSubmit={(e) => { void submit(e); }} className="space-y-4">
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="email"
                  className="stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider"
                >
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={e => { setEmail(e.target.value); }}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2.5 bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-lg focus:ring-1 focus:ring-[var(--stitch-primary)] focus:border-[var(--stitch-primary)] outline-none stitch-font-body text-[14px] text-[var(--stitch-on-surface)]"
                />
              </div>

              {state === 'error' && errorMsg && (
                <div role="alert" className="rounded-lg bg-[var(--color-error-100,#fee2e2)] px-4 py-3 text-sm text-red-700">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={state === 'loading' || !email.trim()}
                className="w-full px-6 py-3 bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] rounded-full stitch-font-label text-[13px] uppercase tracking-widest font-bold hover:bg-[var(--stitch-primary-container)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {state === 'loading' ? 'Sending…' : 'Send sign-in link'}
              </button>
            </form>
          )}

          <p className="mt-6 text-center stitch-font-body text-[12px] text-[var(--stitch-on-surface-variant)]">
            Don't have an account?{' '}
            <a href="/" className="text-[var(--stitch-primary)] hover:underline">
              Get started
            </a>
          </p>
        </div>
      </main>
      <BrandFooter />
    </div>
  );
}
